// ** React Imports
import React, { Suspense, useContext, lazy, Fragment } from 'react';

// ** Utils
import { useLayout } from '@hooks/useLayout';
import { useRouterTransition } from '@hooks/useRouterTransition';

// ** Custom Components
// import Spinner from '@components/spinner/Loading-spinner' // Uncomment if your require content fallback
import LayoutWrapper from '@layouts/layout-wrapper';

// ** Router Components
import { BrowserRouter as AppRouter, Route, Switch, Redirect } from 'react-router-dom';
// import Spinner from '@components/spinner/Fallback-spinner';

// ** Layouts
import BlankLayout from '@layouts/BlankLayout';
import FullLayout from '@layouts/FullLayout';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';

// ** Vertical Menu Array Of Items
import navigation from '@src/navigation/vertical';
import Loading from '@src/components/spin/progress';

const Router = ({ Routes }) => {
  // ** Hooks
  const [layout, setLayout] = useLayout();
  const [transition, setTransition] = useRouterTransition();
  const { t } = useTranslation();

  const auth = JSON.parse(localStorage.getItem('persist:Auth'));
  let accessToken = auth?.accessToken;
  if (accessToken) {
    accessToken = JSON.parse(accessToken);
  }

  const pathNavigation = navigation.filter((el) => !el.header).map((item) => item.navLink);

  // ** ACL Ability Context

  // ** Default Layout
  const DefaultLayout = layout === 'BlankLayout' ? 'BlankLayout' : 'FullLayout';

  // ** All of the available layouts
  const Layouts = { BlankLayout, FullLayout };

  // ** Current Active Item
  const currentActiveItem = null;

  // ** Return Filtered Array of Routes & Paths
  const LayoutRoutesAndPaths = (layout) => {
    const LayoutRoutes = [];
    const LayoutPaths = [];

    if (Routes) {
      Routes.filter((route) => {
        // ** Checks if Route layout or Default layout matches current layout
        if (route.layout === layout || (route.layout === undefined && DefaultLayout === layout)) {
          LayoutRoutes.push(route);
          LayoutPaths.push(route.path);
        }
      });
    }

    return { LayoutRoutes, LayoutPaths };
  };

  // ** Return Route to Render
  const ResolveRoutes = () => {
    return Object.keys(Layouts).map((layout, index) => {
      // ** Convert Layout parameter to Layout Component
      // ? Note: make sure to keep layout and component name equal

      const LayoutTag = Layouts[layout];

      // ** Get Routes and Paths of the Layout
      const { LayoutRoutes, LayoutPaths } = LayoutRoutesAndPaths(layout);

      // ** We have freedom to display different layout for different route
      // ** We have made LayoutTag dynamic based on layout, we can also replace it with the only layout component,
      // ** that we want to implement like VerticalLayout or HorizontalLayout
      // ** We segregated all the routes based on the layouts and Resolved all those routes inside layouts

      // ** RouterProps to pass them to Layouts
      const routerProps = {};

      return (
        <Route path={LayoutPaths} key={index}>
          <LayoutTag
            routerProps={routerProps}
            layout={layout}
            setLayout={setLayout}
            transition={transition}
            setTransition={setTransition}
            currentActiveItem={currentActiveItem}
          >
            <Switch>
              {LayoutRoutes.map((route) => {
                return (
                  <Route
                    key={route.path}
                    path={route.path}
                    exact={route.exact === true}
                    render={(props) => {
                      // ** Assign props to routerProps
                      Object.assign(routerProps, {
                        ...props,
                        meta: route.meta,
                      });

                      return (
                        <Suspense fallback={<Loading />}>
                          <>
                            {/* Layout Wrapper to add classes based on route's layout, appLayout and className */}

                            {route.layout === 'BlankLayout' ? (
                              <>
                                {route.titleI18n || route.title ? (
                                  <Helmet>
                                    <title>
                                      {route.titleI18n ? t(route.titleI18n) : route.title}
                                    </title>
                                  </Helmet>
                                ) : null}
                                <route.component {...props} />
                              </>
                            ) : (
                              <LayoutWrapper
                                layout={DefaultLayout}
                                /* Conditional props */
                                transition={transition}
                                setTransition={setTransition}
                                /*eslint-disable */
                                {...(route.appLayout
                                  ? {
                                      appLayout: route.appLayout,
                                    }
                                  : {})}
                                {...(route.meta
                                  ? {
                                      routeMeta: route.meta,
                                    }
                                  : {})}
                                {...(route.className
                                  ? {
                                      wrapperClass: route.className,
                                    }
                                  : {})}
                                /*eslint-enable */
                              >
                                <Suspense fallback={<Loading />}>
                                  <>
                                    {route.titleI18n || route.title ? (
                                      <Helmet>
                                        <title>
                                          {route.titleI18n ? t(route.titleI18n) : route.title}
                                        </title>
                                      </Helmet>
                                    ) : null}
                                    <route.component {...props} />
                                  </>
                                </Suspense>
                              </LayoutWrapper>
                            )}
                          </>{' '}
                        </Suspense>
                      );
                    }}
                  />
                );
              })}
              {accessToken ? (
                <Route exact path={pathNavigation}>
                  <Redirect to={'/coming-soon'} />
                </Route>
              ) : (
                <Route exact path="*">
                  <Redirect to="/" />
                </Route>
              )}
            </Switch>
          </LayoutTag>
        </Route>
      );
    });
  };

  return (
    <AppRouter basename={process.env.REACT_APP_BASENAME}>
      <Switch>
        {ResolveRoutes()}
        {accessToken && (
          <Route path="*">
            <Redirect to={'/error'} />
          </Route>
        )}
      </Switch>
    </AppRouter>
  );
};

export default Router;
